import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  TextField,
  IconButton,
  Autocomplete,
  Button,
  CircularProgress,
  FormControl,
  Select,
  MenuItem,
  Box,
  Typography,
  Chip
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import SettingsIcon from '@mui/icons-material/Settings';
import AddIcon from '@mui/icons-material/Add';
import { TABLE_CONTAINER_STYLE, NUMERICAL_COLUMN_WIDTH, AMOUNT_COLUMN_WIDTH } from '../../constants/invoiceConstants';
import { formatNumber, debounce } from '../../utils/invoiceCalculations';
import ColumnSettingsPopover from './ColumnSettingsPopover';
import { api } from '../../utils/apiService';

/**
 * Helper function to extract unit name from unit string
 * Example: "NUMBERS (NOS)" -> "NUMBERS"
 * Example: "NOS" -> "NOS"
 */
const getUnitName = (unitString) => {
  if (!unitString) return '';

  // Check if unit has format "NAME (CODE)"
  const match = unitString.match(/^([^(]+)\s*\(/);
  if (match) {
    return match[1].trim(); // Return the name before parentheses
  }

  // If no parentheses, return the original string
  return unitString;
};

/**
 * Component for displaying and managing invoice items in a table with bidirectional calculations
 * 
 * @param {Object} props - Component props
 * @param {Array} props.items - Array of invoice items
 * @param {Object} props.newItem - New item being added
 * @param {Object} props.visibleColumns - Object containing column visibility state
 * @param {Array} props.products - Array of available products
 * @param {Function} props.onItemChange - Function to call when an item is changed
 * @param {Function} props.onRemoveItem - Function to call when an item is removed
 * @param {Function} props.onNewItemChange - Function to call when the new item is changed
 * @param {Function} props.onProductChange - Function to call when a product is selected
 * @param {Function} props.onAddItem - Function to call when a new item is added
 * @param {Function} props.onColumnToggle - Function to call when a column visibility is toggled
 * @param {Function} props.debouncedUpdateItem - Debounced function to update items (optional)
 * @param {Boolean} props.loading - Indicates if the table is in a loading state
 * @param {Function} props.onKeyDown - Function to call when a key is pressed in the table
 * @param {Function} props.onNewItemKeyDown - Function to call when a key is pressed in the new item row
 * @param {Function} props.onFocus - Function to call when an input field is focused
 * @param {Function} props.onSettingsClick - Function to call when the settings icon is clicked
 * @param {String} props.type - Type of invoice: 'sales' or 'purchase' (default: 'sales')
 */
// Using imported constants for consistent column widths

const InvoiceItemsTable = ({
  items,
  newItem,
  visibleColumns,
  products,
  loading,
  onItemChange,
  onRemoveItem,
  onNewItemChange,
  onProductChange,
  onAddItem,
  onColumnToggle,
  debouncedUpdateItem,
  onKeyDown,
  onNewItemKeyDown,
  onFocus,
  onSettingsClick,
  productInputRef,
  type = 'sales' // Default to sales invoice
}) => {
  // Common calculation function to update all dependent values when any field changes
  const calculateItemValues = (item, changedField, newValue) => {
    // Create a copy of the item to work with
    const updatedItem = { ...item };

    // Update the changed field
    updatedItem[changedField] = newValue;

    // Calculate all dependent values based on which field changed
    switch (changedField) {
      case 'quantity':
      case 'price':
        // Recalculate lineTotal when quantity or price changes
        updatedItem.lineTotal = (updatedItem.quantity || 0) * (updatedItem.price || 0);
      // Fall through to recalculate discount amount and other values

      case 'lineTotal':
        // Recalculate discountAmount when lineTotal changes
        updatedItem.discountAmount = (updatedItem.lineTotal || 0) * (updatedItem.discount || 0) / 100;
      // Fall through to recalculate net value and other values

      case 'discount':
        // Recalculate discountAmount when discount percentage changes
        updatedItem.discountAmount = (updatedItem.lineTotal || 0) * (updatedItem.discount || 0) / 100;
      // Fall through to recalculate net value and other values

      case 'discountAmount':
        // Recalculate discount percentage when discountAmount changes
        if (updatedItem.lineTotal > 0) {
          updatedItem.discount = ((updatedItem.discountAmount || 0) * 100) / (updatedItem.lineTotal || 1);
        }
      // Fall through to recalculate net value and other values

      case 'vatRate':
      case 'priceWithVat':
        // If priceWithVat changed, update the price
        if (changedField === 'priceWithVat') {
          // When Price + VAT changes, recalculate the unit price
          // Keep the exact user-entered priceWithVat to avoid floating-point drift
          const userEnteredPriceWithVat = updatedItem.priceWithVat;

          updatedItem.price = (userEnteredPriceWithVat || 0) / (1 + (updatedItem.vatRate || 0) / 100);

          // Round to 2 decimal places for better precision
          updatedItem.price = Math.round(updatedItem.price * 100) / 100;

          // Recalculate lineTotal based on the new (rounded) price
          updatedItem.lineTotal = (updatedItem.quantity || 0) * (updatedItem.price || 0);

          // Recalculate discountAmount based on the new lineTotal
          updatedItem.discountAmount = (updatedItem.lineTotal || 0) * (updatedItem.discount || 0) / 100;

          updatedItem.afterDiscount = (updatedItem.lineTotal || 0) - (updatedItem.discountAmount || 0);

          // Total must reflect the EXACT price-with-VAT the user typed × quantity
          // (not re-derived from the rounded price, which would give e.g. 149.99)
          updatedItem.total = parseFloat(((updatedItem.quantity || 0) * userEnteredPriceWithVat).toFixed(2));

          // VAT amount = grand total minus the excl-VAT amount (residual approach)
          updatedItem.vatAmount = parseFloat((updatedItem.total - updatedItem.afterDiscount).toFixed(2));

          // Pin priceWithVat back to exactly what the user typed
          updatedItem.priceWithVat = userEnteredPriceWithVat;

          // Mark this item as having a user-set priceWithVat so subsequent edits
          // (qty, discount, etc.) continue to use this value instead of re-deriving
          // it from the rounded back-calculated price
          updatedItem._priceWithVatLocked = true;

          // Return early — skip the "always-run" block below so it doesn't overwrite
          // total/vatAmount with values derived from the rounded price
          return updatedItem;

        } else if (changedField === 'vatRate') {
          // If vatRate changed, update priceWithVat
          updatedItem.priceWithVat = (updatedItem.price || 0) * (1 + (updatedItem.vatRate || 0) / 100);

          // Round to 2 decimal places for better precision
          updatedItem.priceWithVat = Math.round(updatedItem.priceWithVat * 100) / 100;
        }
      // Fall through to recalculate all values
    }

    // Always calculate these values regardless of which field changed
    // Calculate net value (lineTotal - discountAmount)
    updatedItem.afterDiscount = (updatedItem.lineTotal || 0) - (updatedItem.discountAmount || 0);

    // Calculate VAT amount
    updatedItem.vatAmount = parseFloat(((updatedItem.afterDiscount || 0) * (updatedItem.vatRate || 0) / 100).toFixed(2));

    // Calculate total (afterDiscount + vatAmount)
    updatedItem.total = parseFloat(((updatedItem.afterDiscount || 0) + (updatedItem.vatAmount || 0)).toFixed(2));

    return updatedItem;
  };

  // Calculate totals for the summary row
  const calculateTotals = () => {
    return items.reduce((totals, item) => {
      // totals.quantity += item.quantity || 0;
      totals.quantity += Number(item.quantity ?? 0);
      totals.lineTotal += item.lineTotal || 0;
      totals.discountAmount += item.discountAmount || 0;
      totals.netValue += (item.lineTotal || 0) - (item.discountAmount || 0);
      totals.vatAmount += item.vatAmount || 0;
      totals.grandTotal += item.total || 0;
      return totals;
    }, {
      quantity: 0,
      lineTotal: 0,
      discountAmount: 0,
      netValue: 0,
      vatAmount: 0,
      grandTotal: 0
    });
  };

  const totals = calculateTotals();
  // State for settings menu and dropdown
  const [settingsAnchorEl, setSettingsAnchorEl] = useState(null);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const settingsOpen = Boolean(settingsAnchorEl);

  // Local display state for priceWithVat inputs — holds raw string while user is typing
  // so backspace/clearing works correctly without being coerced to 0
  const [priceWithVatInputs, setPriceWithVatInputs] = useState({});

  const [unitPriceInputs, setUnitPriceInputs] = useState({});

  // Local state for product search results
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [searchLoading, setSearchLoading] = useState(false);

  const [displayLimit, setDisplayLimit] = useState(20);

  useEffect(() => {
    setDisplayLimit(20);
  }, [searchQuery, isDropdownOpen]);
  const getAutocompleteOptions = () => {
    const fullList = searchQuery ? searchResults : products;
    const sliced = fullList.slice(0, displayLimit);
    if (newItem.product && !sliced.some(opt => opt.id === newItem.product.id)) {
      const selectedInFull = fullList.find(opt => opt.id === newItem.product.id);
      if (selectedInFull) {
        return [selectedInFull, ...sliced];
      } else {
        return [newItem.product, ...sliced];
      }
    }
    return sliced;
  };
  // Debounced search for products
  const debouncedProductSearch = React.useRef(
    debounce(async (query) => {
      if (!query) {
        setSearchResults([]);
        setSearchLoading(false);
        return;
      }
      setSearchLoading(true);
      try {
        const response = await api.get(`/products?page=1&limit=20&search=${encodeURIComponent(query)}`);

        let productsList = [];
        if (response.data) {
          if (Array.isArray(response.data)) {
            productsList = response.data;
          } else if (response.data.data) {
            productsList = Array.isArray(response.data.data)
              ? response.data.data
              : (response.data.data.products || response.data.data.docs || []);
          } else if (response.data.products) {
            productsList = response.data.products;
          } else if (response.data.docs) {
            productsList = response.data.docs;
          }
        }

        console.log('debouncedProductSearch raw data:', response.data);
        console.log('debouncedProductSearch parsed list:', productsList);

        const formatted = productsList.map(product => ({
          id: product._id || product.id,
          name: product.productName || product.name || '',
          description: product.description || '',
          price: product.salesRate || 0,
          altSalesRate: product.altSalesRate || 0,
          vat: product.taxRate !== undefined && product.taxRate !== null ? product.taxRate : 15,
          productCode: product.productCode || '',
          basicUnit: product.basicUnit || '',
          alternateUnit: product.alternateUnit || ''
        }));
        setSearchResults(formatted);
      } catch (error) {
        console.error('Error searching products locally:', error);
      } finally {
        setSearchLoading(false);
      }
    }, 300)
  ).current;

  const handleSearchChange = useCallback((query) => {
    setSearchQuery(query);
    debouncedProductSearch(query);
  }, [debouncedProductSearch]);

  // Handle settings popover close
  const handleSettingsClose = () => {
    setSettingsAnchorEl(null);
  };

  // Handle focus on input fields
  const handleFocus = (event) => {
    event.target.select();
  };

  // Handle input change with immediate visual feedback and debounced updates
  const handleInputChange = (itemId, field, value) => {
    // Simply pass the change to the parent component
    // The parent hook will handle all calculations
    onItemChange(itemId, field, value);
  };

  // Handle blur event to ensure calculations are applied
  const handleBlur = (itemId, field, value) => {
    // Find the item being edited
    const item = items.find(item => item.id === itemId);
    if (!item) return;

    if (field === 'priceWithVat') {
      // When the user edited priceWithVat, calculate all dependent fields BUT
      // never re-derive priceWithVat from the rounded price — preserve the exact
      // value the user typed. We do this by sending ONE bulk update that carries
      // '_sourceField' so the parent calculation knows to skip re-deriving priceWithVat.
      const updatedItem = calculateItemValues(item, field, value);
      // _sourceField tells calculateItemTotals in the hook to keep priceWithVat as-is
      onItemChange(itemId, '_bulkUpdate', { ...updatedItem, _sourceField: 'priceWithVat' });
    } else if (field === 'price') {
      const updatedItem = calculateItemValues(item, field, value);
      updatedItem.priceWithVat = parseFloat(((value || 0) * (1 + (item.vatRate || 0) / 100)).toFixed(2));
      onItemChange(itemId, '_bulkUpdate', { ...updatedItem, _sourceField: 'price' });
    } else {
      // For all other fields: calculate then dispatch each changed key
      const updatedItem = calculateItemValues(item, field, value);

      // Force immediate update on blur for final calculation
      onItemChange(itemId, field, value);

      // Update all calculated fields
      Object.keys(updatedItem).forEach(key => {
        if (key !== field && updatedItem[key] !== item[key]) {
          onItemChange(itemId, key, updatedItem[key]);
        }
      });
    }
  };

  // Handle calculations for new item row
  const handleNewItemCalculation = (field, value) => {
    // Simply pass the change to the parent component
    onNewItemChange(field, value);
  };

  // Handle key down events for navigation and adding items
  const handleKeyDown = (event, itemId, field, tabIndex) => {
    // If dropdown is open, let the Autocomplete handle keyboard events
    if (isDropdownOpen && field === 'product') {
      return;
    }

    // Handle Tab and Enter keys for navigation
    if (event.key === 'Tab' || event.key === 'Enter') {
      if (event.key === 'Enter') {
        event.preventDefault(); // Prevent form submission
      }

      // Focus next input only if not in product dropdown
      const inputs = document.querySelectorAll('input[data-item-id], input[data-new-item-field]');
      const inputsArray = Array.from(inputs);
      const currentIndex = inputsArray.findIndex(
        input => {
          const itemAttr = input.getAttribute('data-item-id');
          const fieldAttr = input.getAttribute('data-field') || input.getAttribute('data-new-item-field');
          return (itemAttr === itemId?.toString() && fieldAttr === field) ||
            (fieldAttr === field && !itemAttr); // For new item fields
        }
      );

      if (currentIndex !== -1) {
        if (event.key === 'Tab') {
          event.preventDefault(); // Prevent default browser tab navigation to avoid double-focus/skips

          if (!event.shiftKey) {
            // Forward Tab
            if (currentIndex < inputsArray.length - 1) {
              const nextInput = inputsArray[currentIndex + 1];
              nextInput.focus();
              nextInput.select?.();
            }
          } else {
            // Backward Tab (Shift + Tab)
            if (currentIndex > 0) {
              const prevInput = inputsArray[currentIndex - 1];
              prevInput.focus();
              prevInput.select?.();
            }
          }
        } else if (event.key === 'Enter') {
          if (currentIndex < inputsArray.length - 1) {
            const nextInput = inputsArray[currentIndex + 1];
            nextInput.focus();
            nextInput.select?.();
          }
        }
      }
    } else if (event.key === 'Escape' && isDropdownOpen) {
      setIsDropdownOpen(false);
    }
  };


  // Handle key down events for new item fields
  const handleNewItemKeyDown = (event, field) => {
    // Handle Tab and Enter keys for navigation
    if (event.key === 'Tab' || event.key === 'Enter') {
      if (event.key === 'Enter') {
        event.preventDefault(); // Prevent form submission
      }

      if (event.key === 'Tab') {
        event.preventDefault(); // Prevent default browser tab navigation to avoid double-focus/skips

        const visibleFields = getVisibleFields();
        const currentIndex = visibleFields.indexOf(field);

        if (!event.shiftKey) {
          // Forward Tab
          if (currentIndex !== -1 && currentIndex < visibleFields.length - 1) {
            const nextField = visibleFields[currentIndex + 1];
            focusOnField(nextField);
          }
        } else {
          // Backward Tab
          if (currentIndex > 0) {
            const prevField = visibleFields[currentIndex - 1];
            focusOnField(prevField);
          }
        }
      } else if (event.key === 'Enter') {
        moveToNextField(field);
      }
    }
  };

  // Helper functions to keep the main function clean
  const getVisibleFields = () => {
    const fields = ['product', 'quantity', 'priceWithVat', 'totalAmt', 'discount', 'vatRate'];
    return fields.filter(f => f !== 'discount' || visibleColumns.discountPerc);
  };

  const focusOnField = (fieldName) => {
    // Special handling for product field
    if (fieldName === 'product') {


      // Get the autocomplete component by ID
      const autocomplete = document.getElementById('product-autocomplete');


      if (autocomplete) {
        // Access the input element inside the Autocomplete
        const autocompleteInput = autocomplete.querySelector('input');


        if (autocompleteInput) {

          // Focus and open the dropdown
          autocompleteInput.focus();
          autocompleteInput.click();


          try {
            // Simulate typing to open the dropdown

            const nativeInputValueSetter = Object.getOwnPropertyDescriptor(window.HTMLInputElement.prototype, 'value').set;
            nativeInputValueSetter.call(autocompleteInput, ' ');
            const inputEvent = new Event('input', { bubbles: true });
            autocompleteInput.dispatchEvent(inputEvent);


            // Clear the space we added
            setTimeout(() => {

              nativeInputValueSetter.call(autocompleteInput, '');
              autocompleteInput.dispatchEvent(inputEvent);

            }, 50);
          } catch (error) {
            console.error("Error in simulating typing:", error);
          }
        }
      } else {

        // Try alternative method if autocomplete is not found
        const field = document.querySelector(`[data-new-item-field="${fieldName}"]`);


        if (field) {
          field.focus();
          field.click();

        }
      }
      return;
    }

    // Standard focus for other fields
    const field = document.querySelector(`[data-new-item-field="${fieldName}"]`);

    if (field) {
      field.focus();
      field.select();
    }
  };

  const focusOnAddButton = () => {
    const addButton = document.querySelector('button[data-add-item="true"]');
    if (addButton) addButton.focus();
  };

  const moveToNextField = (currentField) => {
    const visibleFields = getVisibleFields();
    const currentIndex = visibleFields.indexOf(currentField);

    if (currentIndex !== -1 && currentIndex < visibleFields.length - 1) {
      const nextField = visibleFields[currentIndex + 1];
      focusOnField(nextField);
    }
  };

  return (
    <>
      <TableContainer
        component={Paper}
        elevation={0}
        sx={TABLE_CONTAINER_STYLE}
      >
        <Table size="small">
          <TableHead>
            <TableRow sx={{
              backgroundColor: '#f7fafd',
              '& .MuiTableCell-root': {
                fontWeight: 'bold',
                color: '#1976d2',
                borderBottom: '2px solid rgba(25, 118, 210, 0.2)',
                py: 1.5
              }
            }}>
              <TableCell width="30%" align="left">Item</TableCell>
              <TableCell align="center" width={NUMERICAL_COLUMN_WIDTH}>Qty</TableCell>
              <TableCell align="center" width={NUMERICAL_COLUMN_WIDTH}>Unit</TableCell>
              <TableCell align="center" width={AMOUNT_COLUMN_WIDTH}>Unit Price</TableCell>
              {visibleColumns.priceWithVat && <TableCell align="center" width={AMOUNT_COLUMN_WIDTH}>Price + VAT</TableCell>}
              <TableCell align="center" width={AMOUNT_COLUMN_WIDTH}>Total Amt</TableCell>
              {visibleColumns.discountPerc && <TableCell align="center" width={NUMERICAL_COLUMN_WIDTH}>Discount %</TableCell>}
              {visibleColumns.discountAmt && <TableCell align="center" width={AMOUNT_COLUMN_WIDTH}>Discount Amt</TableCell>}
              {visibleColumns.netValue && <TableCell align="center" width={AMOUNT_COLUMN_WIDTH}>Net Value</TableCell>}
              <TableCell align="center" width={NUMERICAL_COLUMN_WIDTH}>VAT %</TableCell>
              {visibleColumns.vatAmount && <TableCell align="center" width={AMOUNT_COLUMN_WIDTH}>VAT Amt</TableCell>}
              {visibleColumns.grandTotal && <TableCell align="center" width={AMOUNT_COLUMN_WIDTH}>Grand Total</TableCell>}
              {visibleColumns.description && <TableCell align="center" width="150px">Description</TableCell>}
              <TableCell align="center" width="50px">
                <IconButton
                  size="small"
                  onClick={(e) => setSettingsAnchorEl(e.currentTarget)}
                  sx={{ p: 0.5 }}
                  aria-label="Column visibility settings"
                  aria-controls="column-visibility-menu"
                  aria-haspopup="true"
                >
                  <SettingsIcon fontSize="small" />
                </IconButton>
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {/* Existing items */}
            {items.map((item, index) => (
              <TableRow
                key={item.id}
                sx={{
                  backgroundColor: index % 2 === 0 ? 'white' : 'rgba(25, 118, 210, 0.02)',
                  '&:hover': {
                    backgroundColor: 'rgba(25, 118, 210, 0.04)',
                  },
                  transition: 'background-color 0.2s ease',
                  '& .MuiTableCell-root': {
                    py: 0.75 // Decrease vertical padding to reduce row height
                  }
                }}
              >
                <TableCell align="left">
                  {type === 'purchase' && item.product ? (
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.25 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Typography variant="body2" sx={{ fontWeight: 500 }}>
                          {item.product.name}
                        </Typography>
                        {/* {item.product.productCode && (
                          <Chip
                            size="small"
                            label={item.product.productCode}
                            sx={{
                              height: 18,
                              fontSize: '0.7rem',
                              backgroundColor: '#e3f2fd',
                              '& .MuiChip-label': { px: 1 }
                            }}
                          />
                        )} */}
                      </Box>
                      {/* {item.description && item.description !== item.product.name && (
                        <Typography variant="caption" color="text.secondary">
                          {item.description}
                        </Typography>
                      )} */}
                    </Box>
                  ) : (
                    <>
                      {item.product?.name || item.description}
                      {/* Add a hidden span with the product ID for debugging */}
                      <span style={{ display: 'none' }}>
                        ID: {item.product?.id || 'No ID'}
                      </span>
                    </>
                  )}
                </TableCell>
                <TableCell align="center">
                  <TextField
                    value={item.quantity}
                    onChange={(e) => {
                      const newValue = e.target.value;
                      if (newValue === '' || !isNaN(parseInt(newValue))) {
                        handleInputChange(item.id, 'quantity', parseInt(newValue) || 0);
                      }
                    }}
                    onBlur={(e) => handleBlur(item.id, 'quantity', parseInt(e.target.value) || 0)}
                    onKeyDown={(e) => handleKeyDown(e, item.id, 'quantity', 0)}
                    onFocus={(e) => {
                      handleFocus(e);
                      e.target.select();
                    }}
                    type="number"
                    size="small"
                    variant="standard"
                    InputProps={{
                      disableUnderline: true,
                      inputProps: {
                        min: 0,
                        style: { textAlign: 'center' }
                      },
                      inputRef: (input) => {
                        if (input) {
                          input.setAttribute('data-item-id', item.id);
                          input.setAttribute('data-field', 'quantity');
                        }
                      }
                    }}
                    sx={{
                      width: NUMERICAL_COLUMN_WIDTH,
                      '& input::-webkit-outer-spin-button, & input::-webkit-inner-spin-button': {
                        display: 'none',
                      },
                      '& input': {
                        MozAppearance: 'textfield',
                      },
                      '& .MuiInputBase-root': {
                        border: '1px solid transparent',
                        borderRadius: '4px',
                        paddingX: '4px',
                        transition: 'border-color 0.2s ease',
                      },
                      '& .MuiInputBase-root:hover': {
                        borderColor: '#1976d2',
                      },
                      '& .MuiInputBase-root.Mui-focused': {
                        borderColor: '#1976d2',
                        backgroundColor: '#e3f2fd',
                      },
                    }}
                  />
                </TableCell>
                <TableCell align="center">
                  <FormControl size="small" variant="standard" sx={{ minWidth: 100 }}>
                    <Select
                      value={item.unit || item.product?.basicUnit || ''}
                      onChange={(e) => handleInputChange(item.id, 'unit', e.target.value)}
                      onKeyDown={(e) => handleKeyDown(e, item.id, 'unit', 1)}
                      disableUnderline
                      sx={{
                        '& .MuiSelect-select': {
                          py: 0.75,
                          textAlign: 'center',
                          fontSize: '0.875rem'
                        }
                      }}
                    >
                      {item.product?.basicUnit && (
                        <MenuItem value={item.product.basicUnit} sx={{ fontSize: '0.875rem' }}>
                          {getUnitName(item.product.basicUnit)}
                        </MenuItem>
                      )}
                      {item.product?.alternateUnit && (
                        <MenuItem value={item.product.alternateUnit} sx={{ fontSize: '0.875rem' }}>
                          {getUnitName(item.product.alternateUnit)}
                        </MenuItem>
                      )}
                    </Select>
                  </FormControl>
                </TableCell>
                <TableCell align="center">
                  <TextField
                    value={
                      unitPriceInputs[item.id] !== undefined
                        ? unitPriceInputs[item.id]
                        : (
                          item.price !== undefined && item.price !== null && item.price !== ''
                            ? Number(item.price).toFixed(2)
                            : '0.00'
                        )
                    }
                    onChange={(e) => {
                      const newValue = e.target.value;
                      setUnitPriceInputs((prev) => ({ ...prev, [item.id]: newValue }));
                      if (newValue !== '' && !isNaN(parseFloat(newValue))) {
                        handleInputChange(item.id, 'price', parseFloat(newValue));
                      }
                    }}
                    onBlur={(e) => {
                      const committed = parseFloat(e.target.value) || 0;
                      handleBlur(item.id, 'price', committed);
                      setUnitPriceInputs((prev) => {
                        const next = { ...prev };
                        delete next[item.id];
                        return next;
                      });
                    }}
                    onKeyDown={(e) => handleKeyDown(e, item.id, 'price', 1)}
                    onFocus={(e) => {
                      const currentDisplay =
                        item.price !== undefined && item.price !== null && item.price !== ''
                          ? Number(item.price).toFixed(2)
                          : '0.00';
                      setUnitPriceInputs((prev) => ({ ...prev, [item.id]: currentDisplay }));
                      handleFocus(e);
                    }}
                    type="text"
                    inputMode="decimal"
                    size="small"
                    variant="standard"
                    InputProps={{
                      disableUnderline: true,
                      inputProps: {
                        min: 0,
                        step: 0.01,
                        style: { textAlign: 'center' }
                      },
                      inputRef: (input) => {
                        if (input) {
                          input.setAttribute('data-item-id', item.id);
                          input.setAttribute('data-field', 'price');
                        }
                      }
                    }}
                    sx={{
                      width: AMOUNT_COLUMN_WIDTH,
                      '& input::-webkit-outer-spin-button, & input::-webkit-inner-spin-button': {
                        display: 'none',
                      },
                      '& input': {
                        MozAppearance: 'textfield',
                      },
                      '& .MuiInputBase-root': {
                        border: '1px solid transparent',
                        borderRadius: '4px',
                        paddingX: '4px',
                        transition: 'border-color 0.2s ease',
                      },
                      '& .MuiInputBase-root:hover': {
                        borderColor: '#1976d2',
                      },
                      '& .MuiInputBase-root.Mui-focused': {
                        borderColor: '#1976d2',
                        backgroundColor: '#e3f2fd',
                      },
                    }}
                  />
                </TableCell>
                {visibleColumns.priceWithVat && (
                  <TableCell align="center">
                    <TextField
                      // Use local display string when user is actively editing, otherwise show formatted value
                      value={
                        priceWithVatInputs[item.id] !== undefined
                          ? priceWithVatInputs[item.id]
                          : (
                            item.priceWithVat !== undefined && item.priceWithVat !== null && item.priceWithVat !== ''
                              ? Number(item.priceWithVat).toFixed(2)
                              : (item.price * (1 + (item.vatRate || 15) / 100)).toFixed(2)
                          )
                      }
                      onChange={(e) => {
                        const newValue = e.target.value;
                        // Update local display string freely (allows clearing, partial entry)
                        setPriceWithVatInputs((prev) => ({ ...prev, [item.id]: newValue }));
                        // Only propagate a parsed number if the value is a valid number
                        if (newValue !== '' && !isNaN(parseFloat(newValue))) {
                          handleInputChange(item.id, 'priceWithVat', parseFloat(newValue));
                        }
                      }}
                      onBlur={(e) => {
                        // On blur: commit the final parsed value (default 0 if empty)
                        const committed = parseFloat(e.target.value) || 0;
                        handleBlur(item.id, 'priceWithVat', committed);
                        // Clear local display state so the field reverts to the formatted model value
                        setPriceWithVatInputs((prev) => {
                          const next = { ...prev };
                          delete next[item.id];
                          return next;
                        });
                      }}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') {
                          e.preventDefault();
                          const addButton = document.querySelector('button[data-add-item="true"]');
                          if (addButton) {
                            addButton.focus();
                          }
                        } else {
                          handleKeyDown(e, item.id, 'priceWithVat', 1);
                        }
                      }}
                      onFocus={(e) => {
                        // Initialise the local display string with the current formatted value
                        const currentDisplay =
                          item.priceWithVat !== undefined && item.priceWithVat !== null && item.priceWithVat !== ''
                            ? Number(item.priceWithVat).toFixed(2)
                            : (item.price * (1 + (item.vatRate || 15) / 100)).toFixed(2);
                        setPriceWithVatInputs((prev) => ({ ...prev, [item.id]: currentDisplay }));
                        handleFocus(e);
                        // Do NOT auto-select so Tab-in doesn't highlight the whole value
                      }}
                      type="text"
                      inputMode="decimal"
                      size="small"
                      variant="standard"
                      InputProps={{
                        disableUnderline: true,
                        inputProps: {
                          min: 0,
                          step: 0.01,
                          style: { textAlign: 'center' }
                        },
                        inputRef: (input) => {
                          if (input) {
                            input.setAttribute('data-item-id', item.id);
                            input.setAttribute('data-field', 'priceWithVat');
                          }
                        }
                      }}
                      sx={{
                        width: AMOUNT_COLUMN_WIDTH,
                        '& .MuiInputBase-root': {
                          border: '1px solid transparent',
                          borderRadius: '4px',
                          paddingX: '4px',
                          transition: 'border-color 0.2s ease',
                        },
                        '& .MuiInputBase-root:hover': {
                          borderColor: '#1976d2',
                        },
                        '& .MuiInputBase-root.Mui-focused': {
                          borderColor: '#1976d2',
                          backgroundColor: '#e3f2fd',
                        },
                      }}
                    />
                  </TableCell>
                )}
                <TableCell align="center">
                  <TextField
                    value={formatNumber(item.lineTotal)}
                    type="text"
                    size="small"
                    variant="standard"
                    InputProps={{
                      readOnly: true,
                      disableUnderline: true,
                      inputProps: {
                        style: { textAlign: 'center' }
                      },
                      inputRef: (input) => {
                        if (input) {
                          input.setAttribute('data-item-id', item.id);
                          input.setAttribute('data-field', 'totalAmt');
                        }
                      }
                    }}
                    sx={{
                      width: AMOUNT_COLUMN_WIDTH,
                      '& input::-webkit-outer-spin-button, & input::-webkit-inner-spin-button': {
                        display: 'none',
                      },
                      '& input': {
                        MozAppearance: 'textfield',
                      },
                      '& .MuiInputBase-root': {
                        border: '1px solid transparent',
                        borderRadius: '4px',
                        paddingX: '4px',
                        transition: 'border-color 0.2s ease',
                      },
                      '& .MuiInputBase-root:hover': {
                        borderColor: '#1976d2',
                      },
                      '& .MuiInputBase-root.Mui-focused': {
                        borderColor: '#1976d2',
                        backgroundColor: '#e3f2fd',
                      },
                    }}
                  />
                </TableCell>
                {visibleColumns.discountPerc && (
                  <TableCell align="center">
                    <TextField
                      value={item.discount || 0}
                      onChange={(e) => {
                        const newValue = e.target.value;
                        // Only update with valid numbers
                        if (newValue === '' || !isNaN(parseFloat(newValue))) {
                          handleInputChange(item.id, 'discount', parseFloat(newValue) || 0);
                        }
                      }}
                      onBlur={(e) => handleBlur(item.id, 'discount', parseFloat(e.target.value) || 0)}
                      onKeyDown={(e) => handleKeyDown(e, item.id, 'discount', 4)}
                      onFocus={(e) => {
                        handleFocus(e);
                        e.target.select(); // Select all text on focus for easier editing
                      }}
                      type="number"
                      size="small"
                      variant="standard"
                      InputProps={{
                        disableUnderline: true,
                        inputProps: {
                          min: 0,
                          max: 100,
                          style: { textAlign: 'center' } // Center the text inside input
                        },
                        inputRef: (input) => {
                          if (input) {
                            input.setAttribute('data-item-id', item.id);
                            input.setAttribute('data-field', 'discount');
                          }
                        }
                      }}
                      sx={{
                        width: NUMERICAL_COLUMN_WIDTH,
                        '& input::-webkit-outer-spin-button, & input::-webkit-inner-spin-button': {
                          display: 'none',
                        },
                        '& input': {
                          MozAppearance: 'textfield',
                        },
                        '& .MuiInputBase-root': {
                          border: '1px solid transparent',
                          borderRadius: '4px',
                          paddingX: '4px',
                          transition: 'border-color 0.2s ease',
                        },
                        '& .MuiInputBase-root:hover': {
                          borderColor: '#1976d2',
                        },
                        '& .MuiInputBase-root.Mui-focused': {
                          borderColor: '#1976d2',
                          backgroundColor: '#e3f2fd',
                        },
                      }}
                    />
                  </TableCell>
                )}
                {visibleColumns.discountAmt && (
                  <TableCell align="center">
                    <TextField
                      value={item.discountAmount || 0}
                      onChange={(e) => {
                        const newValue = e.target.value;
                        // Only update with valid numbers
                        if (newValue === '' || !isNaN(parseFloat(newValue))) {
                          handleInputChange(item.id, 'discountAmount', parseFloat(newValue) || 0);
                        }
                      }}
                      onBlur={(e) => handleBlur(item.id, 'discountAmount', parseFloat(e.target.value) || 0)}
                      onKeyDown={(e) => handleKeyDown(e, item.id, 'discountAmount', 5)}
                      onFocus={(e) => {
                        handleFocus(e);
                        e.target.select(); // Select all text on focus for easier editing
                      }}
                      type="number"
                      size="small"
                      variant="standard"
                      InputProps={{
                        disableUnderline: true,
                        inputProps: {
                          min: 0,
                          step: 0.01,
                          style: { textAlign: 'center' } // Center the text inside input
                        },
                        inputRef: (input) => {
                          if (input) {
                            input.setAttribute('data-item-id', item.id);
                            input.setAttribute('data-field', 'discountAmount');
                          }
                        }
                      }}
                      sx={{
                        width: AMOUNT_COLUMN_WIDTH,
                        '& input::-webkit-outer-spin-button, & input::-webkit-inner-spin-button': {
                          display: 'none',
                        },
                        '& input': {
                          MozAppearance: 'textfield',
                        },
                        '& .MuiInputBase-root': {
                          border: '1px solid transparent',
                          borderRadius: '4px',
                          paddingX: '4px',
                          transition: 'border-color 0.2s ease',
                        },
                        '& .MuiInputBase-root:hover': {
                          borderColor: '#1976d2',
                        },
                        '& .MuiInputBase-root.Mui-focused': {
                          borderColor: '#1976d2',
                          backgroundColor: '#e3f2fd',
                        },
                      }}
                    />
                  </TableCell>
                )}
                {visibleColumns.netValue && (
                  <TableCell align="center">
                    {formatNumber(item.afterDiscount)}
                  </TableCell>
                )}
                <TableCell align="center">
                  <TextField
                    value={formatNumber(item.vatRate)}
                    type="text"
                    size="small"
                    variant="standard"
                    InputProps={{
                      readOnly: true,
                      disableUnderline: true,
                      inputProps: {
                        style: { textAlign: 'center' }
                      },
                      endAdornment: '%'
                    }}
                    sx={{
                      width: NUMERICAL_COLUMN_WIDTH,
                      '& input::-webkit-outer-spin-button, & input::-webkit-inner-spin-button': {
                        display: 'none',
                      },
                      '& input': {
                        MozAppearance: 'textfield',
                      }
                    }}
                  />
                </TableCell>
                {visibleColumns.vatAmount && (
                  <TableCell align="center">
                    {formatNumber(item.vatAmount)}
                  </TableCell>
                )}
                {visibleColumns.grandTotal && (
                  <TableCell align="center">
                    {formatNumber(item.total)}
                  </TableCell>
                )}
                {visibleColumns.description && (
                  <TableCell align="center">
                    <TextField
                      value={item.description}
                      onChange={(e) => onItemChange(item.id, 'description', e.target.value)}
                      size="small"
                      variant="standard"
                      fullWidth
                      InputProps={{
                        disableUnderline: true
                      }}
                      sx={{
                        '& .MuiInputBase-root': {
                          border: '1px solid transparent',
                          borderRadius: '4px',
                          paddingX: '4px',
                          transition: 'border-color 0.2s ease',
                        },
                        '& .MuiInputBase-root:hover': {
                          borderColor: '#1976d2',
                        },
                        '& .MuiInputBase-root.Mui-focused': {
                          borderColor: '#1976d2',
                          backgroundColor: '#e3f2fd',
                        },
                      }}
                    />
                  </TableCell>
                )}
                <TableCell align="center">
                  <IconButton size="small" onClick={() => onRemoveItem(item.id)} disabled={loading}>
                    <DeleteIcon fontSize="small" />
                  </IconButton>
                </TableCell>
              </TableRow>
            ))}

            {/* Add new item row */}
            <TableRow sx={{
              bgcolor: '#f0f7ff',
              borderTop: '1px dashed rgba(25, 118, 210, 0.2)',
              '& .MuiTableCell-root': {
                py: 0.75 // Decrease vertical padding to reduce row height
              },
              '&:hover': {
                bgcolor: '#e3f2fd',
              },
              transition: 'background-color 0.2s ease'
            }}>
              <TableCell align="left">
                <Autocomplete
                  key={newItem.product ? 'product-selected' : 'product-empty'}
                  id="product-autocomplete"
                  options={getAutocompleteOptions()}

                  getOptionLabel={(option) => option?.name || ''}
                  value={newItem.product}
                  onChange={(event, value, reason) => {
                    console.log('Autocomplete onChange:', { value: value?.name, reason, eventType: event?.type });

                    // Check if product is selected (both keyboard and mouse)
                    const isProductSelected = value && (reason === 'selectOption' || reason === 'blur');
                    const isEnterKeySelection = isProductSelected && (event?.type === 'keydown' || reason === 'blur');
                    console.log('Product selected:', { isProductSelected, isEnterKeySelection });

                    // Call onProductChange with auto-add flag for both keyboard and mouse selection
                    onProductChange(event, value, isProductSelected);

                    // Focus quantity field after product selection (mouse click only)
                    if (value && !isEnterKeySelection) {
                      setTimeout(() => {
                        const qtyField = document.querySelector('[data-new-item-field="quantity"]');
                        if (qtyField) {
                          qtyField.focus();
                          qtyField.select();
                        }
                      }, 100);
                    }
                  }}
                  onInputChange={(event, newInputValue, reason) => {
                    console.log('Autocomplete onInputChange:', { newInputValue, reason });
                    if (reason === 'input') {
                      handleSearchChange(newInputValue);
                    } else if (reason === 'clear' || newInputValue === '') {
                      handleSearchChange('');
                    } else if (reason === 'reset') {
                      setSearchQuery('');
                      setSearchResults([]);
                    }
                  }}
                  filterOptions={(x) => x}
                  loading={loading || searchLoading}
                  onKeyDown={(e) => onNewItemKeyDown(e, 'product')}
                  size="small"
                  disabled={loading}
                  openOnFocus
                  autoHighlight
                  autoSelect
                  disableCloseOnSelect={false}
                  clearOnBlur={false}
                  blurOnSelect
                  isOptionEqualToValue={(option, value) => {
                    if (!option || !value) return false;
                    return option.id === value.id;
                  }}
                  onOpen={() => setIsDropdownOpen(true)}
                  onClose={() => setIsDropdownOpen(false)}
                  onBlur={(e) => {
                    // Auto-add item if product is selected and we're blurring away
                    if (newItem.product) {
                      // Small delay to ensure focus has moved away
                      setTimeout(() => {
                        // Check if focus is not on any new-item field
                        const activeElement = document.activeElement;
                        const isNewItemField = activeElement?.getAttribute('data-new-item-field');

                        if (!isNewItemField) {
                          // Focus has moved outside the new item row, auto-add
                          onAddItem();
                        }
                      }, 100);
                    }
                  }}
                  renderOption={(props, option) => (
                    <li {...props} key={option.id} style={{ fontSize: '0.75rem', padding: '4px 8px', fontWeight: 600 }}>
                      {option.name} - {option.productCode || 'No Code'}
                    </li>
                  )}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      placeholder="Select Product"
                      variant="standard"
                      InputProps={{
                        ...params.InputProps,
                        disableUnderline: true
                      }}
                      inputRef={(input) => {
                        if (input) {
                          input.setAttribute('data-new-item-field', 'product');
                          // Store ref for auto-focus
                          if (productInputRef) {
                            productInputRef.current = input;
                          }
                        }
                      }}
                      onFocus={(e) => onFocus && onFocus(e)}
                      sx={{
                        '& .MuiInputBase-root': {
                          border: '1px solid transparent',
                          borderRadius: '4px',
                          paddingX: '4px',
                          transition: 'border-color 0.2s ease',
                        },
                        '& .MuiInputBase-root:hover': {
                          borderColor: '#1976d2',
                        },
                        '& .MuiInputBase-root.Mui-focused': {
                          borderColor: '#1976d2',
                          backgroundColor: '#e3f2fd',
                        },
                      }}
                    />
                  )}
                />
              </TableCell>
              <TableCell align="center">
                <TextField
                  value={newItem.quantity}
                  onChange={(e) => {
                    const newValue = e.target.value;
                    if (newValue === '' || !isNaN(parseInt(newValue))) {
                      handleNewItemCalculation('quantity', parseInt(newValue) || 0);
                    }
                  }}
                  onKeyDown={(e) => onNewItemKeyDown(e, 'quantity')}
                  onFocus={(e) => {
                    handleFocus(e);
                    e.target.select();
                  }}
                  type="number"
                  size="small"
                  variant="standard"
                  InputProps={{
                    disableUnderline: true,
                    inputProps: {
                      min: 0,
                      style: { textAlign: 'center' }
                    },
                    inputRef: (input) => {
                      if (input) {
                        input.setAttribute('data-new-item-field', 'quantity');
                      }
                    }
                  }}
                  sx={{
                    width: NUMERICAL_COLUMN_WIDTH,
                    '& input::-webkit-outer-spin-button, & input::-webkit-inner-spin-button': {
                      display: 'none',
                    },
                    '& input': {
                      MozAppearance: 'textfield',
                    },
                    '& .MuiInputBase-root': {
                      border: '1px solid transparent',
                      borderRadius: '4px',
                      paddingX: '4px',
                      transition: 'border-color 0.2s ease',
                    },
                    '& .MuiInputBase-root:hover': {
                      borderColor: '#1976d2',
                    },
                    '& .MuiInputBase-root.Mui-focused': {
                      borderColor: '#1976d2',
                      backgroundColor: '#e3f2fd',
                    }
                  }}
                />
              </TableCell>
              <TableCell align="center">
                <FormControl size="small" variant="standard" sx={{ minWidth: 100 }}>
                  <Select
                    value={newItem.unit || newItem.product?.basicUnit || ''}
                    onChange={(e) => handleNewItemCalculation('unit', e.target.value)}
                    onKeyDown={(e) => onNewItemKeyDown(e, 'unit')}
                    disableUnderline
                    sx={{
                      '& .MuiSelect-select': {
                        py: 0.75,
                        textAlign: 'center',
                        fontSize: '0.875rem'
                      }
                    }}
                  >
                    {newItem.product?.basicUnit && (
                      <MenuItem value={newItem.product.basicUnit} sx={{ fontSize: '0.875rem' }}>
                        {getUnitName(newItem.product.basicUnit)}
                      </MenuItem>
                    )}
                    {newItem.product?.alternateUnit && (
                      <MenuItem value={newItem.product.alternateUnit} sx={{ fontSize: '0.875rem' }}>
                        {getUnitName(newItem.product.alternateUnit)}
                      </MenuItem>
                    )}
                  </Select>
                </FormControl>
              </TableCell>
              <TableCell align="center">
                <TextField
                  value={formatNumber(newItem.price)}
                  type="text"
                  size="small"
                  variant="standard"
                  InputProps={{
                    readOnly: true,
                    disableUnderline: true,
                    inputProps: {
                      style: { textAlign: 'center' }
                    }
                  }}
                  sx={{
                    width: AMOUNT_COLUMN_WIDTH,
                    '& input::-webkit-outer-spin-button, & input::-webkit-inner-spin-button': {
                      display: 'none',
                    },
                    '& input': {
                      MozAppearance: 'textfield',
                    }
                  }}
                />
              </TableCell>
              {visibleColumns.priceWithVat && (
                <TableCell align="center">
                  <TextField
                    value={
                      newItem.priceWithVat !== undefined && newItem.priceWithVat !== null && newItem.priceWithVat !== ''
                        ? Number(newItem.priceWithVat).toFixed(2)
                        : (newItem.price * (1 + (newItem.vatRate || 15) / 100)).toFixed(2)
                    }
                    onChange={(e) => {
                      const newValue = e.target.value;
                      // Only update with valid numbers
                      if (newValue === '' || !isNaN(parseFloat(newValue))) {
                        const value = parseFloat(newValue) || 0;
                        handleNewItemCalculation('priceWithVat', value);
                      }
                    }}
                    onKeyDown={(e) => handleNewItemKeyDown(e, 'priceWithVat')}
                    onFocus={(e) => {
                      handleFocus(e);
                      e.target.select(); // Select all text on focus for easier editing
                    }}
                    type="number"
                    size="small"
                    variant="standard"
                    InputProps={{
                      disableUnderline: true,
                      inputProps: {
                        min: 0,
                        step: 0.01,
                        style: { textAlign: 'center' } // Center the text inside input
                      },
                      inputRef: (input) => {
                        if (input) {
                          input.setAttribute('data-new-item-field', 'priceWithVat');
                        }
                      }
                    }}
                    sx={{
                      width: AMOUNT_COLUMN_WIDTH,
                      '& input::-webkit-outer-spin-button, & input::-webkit-inner-spin-button': {
                        display: 'none',
                      },
                      '& input': {
                        MozAppearance: 'textfield',
                      },
                      '& .MuiInputBase-root': {
                        border: '1px solid transparent',
                        borderRadius: '4px',
                        paddingX: '4px',
                        transition: 'border-color 0.2s ease',
                      },
                      '& .MuiInputBase-root:hover': {
                        borderColor: '#1976d2',
                      },
                      '& .MuiInputBase-root.Mui-focused': {
                        borderColor: '#1976d2',
                        backgroundColor: '#e3f2fd',
                      }
                    }}
                  />
                </TableCell>
              )}
              <TableCell align="center">
                <TextField
                  value={newItem.lineTotal?.toFixed(2) || '0.00'}
                  type="text"
                  size="small"
                  variant="standard"
                  InputProps={{
                    readOnly: true,
                    disableUnderline: true,
                    inputProps: {
                      style: { textAlign: 'center' }
                    },
                    inputRef: (input) => {
                      if (input) {
                        input.setAttribute('data-new-item-field', 'totalAmt');
                      }
                    }
                  }}
                  sx={{
                    width: AMOUNT_COLUMN_WIDTH,
                    '& input::-webkit-outer-spin-button, & input::-webkit-inner-spin-button': {
                      display: 'none',
                    },
                    '& input': {
                      MozAppearance: 'textfield',
                    }
                  }}
                />
              </TableCell>
              {visibleColumns.discountPerc && (
                <TableCell align="center">
                  <TextField
                    value={newItem.discount}
                    onChange={(e) => {
                      const newValue = e.target.value;
                      // Only update with valid numbers
                      if (newValue === '' || !isNaN(parseFloat(newValue))) {
                        const value = parseFloat(newValue) || 0;
                        handleNewItemCalculation('discount', value);
                      }
                    }}
                    onKeyDown={(e) => handleNewItemKeyDown(e, 'discount')}
                    onFocus={(e) => {
                      handleFocus(e);
                      e.target.select(); // Select all text on focus for easier editing
                    }}
                    type="number"
                    size="small"
                    variant="standard"
                    InputProps={{
                      disableUnderline: true,
                      inputProps: {
                        min: 0,
                        max: 100,
                        style: { textAlign: 'center' } // Center the text inside input
                      },
                      inputRef: (input) => {
                        if (input) {
                          input.setAttribute('data-new-item-field', 'discount');
                        }
                      }
                    }}
                    sx={{
                      width: NUMERICAL_COLUMN_WIDTH,
                      '& input::-webkit-outer-spin-button, & input::-webkit-inner-spin-button': {
                        display: 'none',
                      },
                      '& input': {
                        MozAppearance: 'textfield',
                      },
                      '& .MuiInputBase-root': {
                        border: '1px solid transparent',
                        borderRadius: '4px',
                        paddingX: '4px',
                        transition: 'border-color 0.2s ease',
                      },
                      '& .MuiInputBase-root:hover': {
                        borderColor: '#1976d2',
                      },
                      '& .MuiInputBase-root.Mui-focused': {
                        borderColor: '#1976d2',
                        backgroundColor: '#e3f2fd',
                      }
                    }}
                  />
                </TableCell>
              )}
              {visibleColumns.discountAmt && (
                <TableCell align="center">
                  <TextField
                    value={newItem.discountAmount || 0}
                    onChange={(e) => {
                      const newValue = e.target.value;
                      // Only update with valid numbers
                      if (newValue === '' || !isNaN(parseFloat(newValue))) {
                        const value = parseFloat(newValue) || 0;
                        handleNewItemCalculation('discountAmount', value);
                      }
                    }}
                    onKeyDown={(e) => handleNewItemKeyDown(e, 'discountAmount')}
                    onFocus={(e) => {
                      handleFocus(e);
                      e.target.select(); // Select all text on focus for easier editing
                    }}
                    type="number"
                    size="small"
                    variant="standard"
                    InputProps={{
                      disableUnderline: true,
                      inputProps: {
                        min: 0,
                        step: 0.01,
                        style: { textAlign: 'center' } // Center the text inside input
                      },
                      inputRef: (input) => {
                        if (input) {
                          input.setAttribute('data-new-item-field', 'discountAmount');
                        }
                      }
                    }}
                    sx={{
                      width: AMOUNT_COLUMN_WIDTH,
                      '& input::-webkit-outer-spin-button, & input::-webkit-inner-spin-button': {
                        display: 'none',
                      },
                      '& input': {
                        MozAppearance: 'textfield',
                      },
                      '& .MuiInputBase-root': {
                        border: '1px solid transparent',
                        borderRadius: '4px',
                        paddingX: '4px',
                        transition: 'border-color 0.2s ease',
                      },
                      '& .MuiInputBase-root:hover': {
                        borderColor: '#1976d2',
                      },
                      '& .MuiInputBase-root.Mui-focused': {
                        borderColor: '#1976d2',
                        backgroundColor: '#e3f2fd',
                      }
                    }}
                  />
                </TableCell>
              )}
              {visibleColumns.netValue && (
                <TableCell align="center">
                  {(newItem.afterDiscount || 0).toFixed(2)}
                </TableCell>
              )}
              <TableCell align="center">
                <TextField
                  value={newItem.vatRate}
                  type="text"
                  size="small"
                  variant="standard"
                  InputProps={{
                    readOnly: true,
                    disableUnderline: true,
                    inputProps: {
                      style: { textAlign: 'center' }
                    },
                    endAdornment: '%'
                  }}
                  sx={{
                    width: NUMERICAL_COLUMN_WIDTH,
                    '& input::-webkit-outer-spin-button, & input::-webkit-inner-spin-button': {
                      display: 'none',
                    },
                    '& input': {
                      MozAppearance: 'textfield',
                    },
                    '& .MuiInputBase-root': {
                      border: '1px solid transparent',
                      borderRadius: '4px',
                      paddingX: '4px',
                      transition: 'border-color 0.2s ease',
                    },
                    '& .MuiInputBase-root:hover': {
                      borderColor: '#1976d2',
                    },
                    '& .MuiInputBase-root.Mui-focused': {
                      borderColor: '#1976d2',
                      backgroundColor: '#e3f2fd',
                    }
                  }}
                />
              </TableCell>
              {visibleColumns.vatAmount && (
                <TableCell align="center">
                  {(newItem.vatAmount || 0).toFixed(2)}
                </TableCell>
              )}
              {visibleColumns.grandTotal && (
                <TableCell align="center">
                  {(newItem.total || 0).toFixed(2)}
                </TableCell>
              )}
              {visibleColumns.description && (
                <TableCell align="center">
                  <TextField
                    value={newItem.description}
                    onChange={(e) => onNewItemChange('description', e.target.value)}
                    size="small"
                    variant="standard"
                    fullWidth
                  />
                </TableCell>
              )}
              <TableCell align="center">
                <Button
                  id="add-item-button"
                  variant="contained"
                  size="small"
                  onClick={onAddItem}
                  disabled={loading || !newItem.product}
                  data-add-item="true"
                  sx={{
                    minWidth: '30px',
                    height: '30px',
                    p: 0,
                    borderRadius: '4px',
                    boxShadow: 'none',
                    '&:hover': {
                      boxShadow: '0 2px 5px rgba(0,0,0,0.1)'
                    }
                  }}
                >
                  {loading ? <CircularProgress size={20} /> : <AddIcon fontSize="small" />}
                </Button>
              </TableCell>
            </TableRow>

            {/* Total calculation row */}
            {items.length > 0 && (
              <TableRow sx={{
                bgcolor: '#f7fafd',
                position: 'sticky',
                bottom: 0,
                zIndex: 3,
                boxShadow: '0 -2px 5px rgba(0,0,0,0.05)',
                '& .MuiTableCell-root': {
                  fontWeight: 'bold',
                  color: '#1976d2',
                  borderTop: '2px solid rgba(25, 118, 210, 0.2)',
                  py: 1.5
                }
              }}>
                <TableCell align="left">Total</TableCell>
                <TableCell align="center">{totals.quantity}</TableCell>
                {type === 'sales' && <TableCell align="center">-</TableCell>}
                <TableCell align="center">-</TableCell>
                {visibleColumns.priceWithVat && <TableCell align="center">-</TableCell>}
                <TableCell align="center">{formatNumber(totals.lineTotal)}</TableCell>
                {visibleColumns.discountPerc && <TableCell align="center">-</TableCell>}
                {visibleColumns.discountAmt && <TableCell align="center">{formatNumber(totals.discountAmount)}</TableCell>}
                {visibleColumns.netValue && <TableCell align="center">{formatNumber(totals.netValue)}</TableCell>}
                <TableCell align="center">-</TableCell>
                {visibleColumns.vatAmount && <TableCell align="center">{formatNumber(totals.vatAmount)}</TableCell>}
                {visibleColumns.grandTotal && <TableCell align="center">{formatNumber(totals.grandTotal)}</TableCell>}
                {visibleColumns.description && <TableCell align="center">-</TableCell>}
                <TableCell align="center"></TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Column Settings Popover */}
      <ColumnSettingsPopover
        open={settingsOpen}
        anchorEl={settingsAnchorEl}
        onClose={handleSettingsClose}
        visibleColumns={visibleColumns}
        onColumnToggle={onColumnToggle}
      />
    </>
  );
};

export default InvoiceItemsTable;
